// src/components/InventoryView.tsx
import React, { useState } from 'react';
import { Database, Search, Filter, Trash2, Download, Plus, ClipboardCopy, AlertTriangle, Loader2, Merge, ArrowUpDown, Pencil } from 'lucide-react';
import { formatPrice, getCategoryColor } from '../utils/formatters';

interface InventoryViewProps {
    inventory: any[];
    
    // States & setters (worden nog steeds in App.tsx beheerd zodat we ze elders kunnen gebruiken)
    inventorySearch: string;
    setInventorySearch: (val: string) => void;
    inventoryFilter: string;
    setInventoryFilter: (val: string) => void;
    
    selectedInventoryIds: Set<string>;
    toggleInventorySelection: (id: string) => void;
    handleSelectAllInventory: (filteredList: any[]) => void;
    handleBulkDeleteInventory: () => void;
    
    // Import/Export/Acties
    handleInventoryExport: () => void;
    openItemModal: (item: any) => void;
    setImportText: (val: string) => void;
    setShowImportModal: (val: boolean) => void;
    
    // Samenvoegen (Duplicates)
    duplicateInventoryGroups: any[][];
    isProcessingImport: boolean;
    mergeAllInventoryGroups: () => void;
    mergeInventoryGroup: (group: any[]) => void;
}

export default function InventoryView({
    inventory,
    inventorySearch, setInventorySearch,
    inventoryFilter, setInventoryFilter,
    selectedInventoryIds, toggleInventorySelection, handleSelectAllInventory, handleBulkDeleteInventory,
    handleInventoryExport, openItemModal, setImportText, setShowImportModal,
    duplicateInventoryGroups, isProcessingImport, mergeAllInventoryGroups, mergeInventoryGroup
}: InventoryViewProps) {

    // --- LOKALE STATE ---
    const [inventorySortConfig, setInventorySortConfig] = useState<{key: string, direction: string} | null>(null);

    // --- LOKALE FUNCTIE ---
    const requestInventorySort = (key: string) => {
        let direction = 'ascending';
        if (inventorySortConfig?.key === key && inventorySortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setInventorySortConfig({ key, direction });
    };

    // --- BEREKENINGEN ---
const uniqueCategories = ["Alle", ...Array.from(new Set(inventory.map(i => i.category || 'Onbekend')))].sort();    let filteredInventory = inventory.filter(item => {
        if (inventoryFilter !== 'Alle' && item.category !== inventoryFilter) return false;
        if (inventorySearch) {
             const searchLower = inventorySearch.toLowerCase();
             const brand = (item.brand || '').toLowerCase();
             const model = (item.model || '').toLowerCase();
             return brand.includes(searchLower) || model.includes(searchLower);
        }
        return true;
    });

    if (inventorySortConfig !== null) {
      filteredInventory.sort((a, b) => {
        let valA = a[inventorySortConfig.key];
        let valB = b[inventorySortConfig.key];
        
        if (inventorySortConfig.key === 'stock' || inventorySortConfig.key === 'price') {
            valA = parseFloat(valA) || 0;
            valB = parseFloat(valB) || 0;
        } else {
            valA = (valA || '').toString().toLowerCase();
            valB = (valB || '').toString().toLowerCase();
        }

        if (valA < valB) return inventorySortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return inventorySortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    const allSelected = filteredInventory.length > 0 && filteredInventory.every(item => selectedInventoryIds.has(item.id));

    // --- WEERGAVE ---
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
              <div>
                  <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center"><Database className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" />Materialen <span className="ml-2 text-slate-400 text-sm font-normal">({filteredInventory.length})</span></h2>
              </div>
              
              <div className="flex flex-wrap gap-2 items-center">
                  <div className="relative">
                      <input 
                        type="text" 
                        placeholder="Zoek materiaal..." 
                        className="bg-slate-100 dark:bg-slate-700 border-none text-slate-900 dark:text-white py-2 pl-9 pr-4 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-40"
                        value={inventorySearch}
                        onChange={(e) => setInventorySearch(e.target.value)}
                      />
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  <div className="relative">
                      <select 
                        value={inventoryFilter} 
                        onChange={(e) => setInventoryFilter(e.target.value)}
                        className="appearance-none bg-slate-100 dark:bg-slate-700 border-none text-slate-700 dark:text-slate-200 py-2 pl-4 pr-8 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                      >
                        {uniqueCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      <Filter className="w-4 h-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  {selectedInventoryIds.size > 0 && (
                        <button 
                            onClick={handleBulkDeleteInventory}
                            className="flex items-center px-4 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/50 transition"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Verwijder ({selectedInventoryIds.size})
                        </button>
                    )}
                  
                  <button onClick={handleInventoryExport} className="flex items-center px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-800"><Download className="w-4 h-4 mr-2" />Export</button>

                  <button onClick={() => openItemModal({})} className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 shadow-md"><Plus className="w-4 h-4 mr-2" />Handmatig</button>
                  <button onClick={() => { setImportText(''); setShowImportModal(true); }} className="flex items-center px-4 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-800"><ClipboardCopy className="w-4 h-4 mr-2" />Import</button>
              </div>
          </div>
          
          {duplicateInventoryGroups.length > 0 && (
              <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                      <h3 className="font-bold text-orange-800 dark:text-orange-200 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2" /> 
                          Mogelijke dubbele materialen gevonden ({duplicateInventoryGroups.length})
                      </h3>
                      <button 
                        onClick={mergeAllInventoryGroups}
                        disabled={isProcessingImport}
                        className="text-xs bg-orange-200 dark:bg-orange-800 hover:bg-orange-300 dark:hover:bg-orange-700 text-orange-900 dark:text-orange-100 px-3 py-1.5 rounded-lg font-bold transition flex items-center"
                      >
                          {isProcessingImport ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <Merge className="w-3 h-3 mr-1"/>}
                          Alles Samenvoegen
                      </button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                      {duplicateInventoryGroups.map((group, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm bg-white dark:bg-slate-800 p-2 rounded-lg border border-orange-100 dark:border-orange-900/50">
                              <span className="text-slate-700 dark:text-slate-300">
                                  <strong>{group[0].brand} {group[0].model}</strong> komt {group.length} keer voor.
                              </span>
                              <button 
                                 onClick={() => mergeInventoryGroup(group)}
                                 className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                              >
                                  Samenvoegen
                              </button>
                          </div>
                      ))}
                  </div>
              </div>
          )}

          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600 dark:text-slate-300">
                  <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-900/50">
                    <tr>
                        <th className="px-4 py-3 w-10">
                            <input 
                                type="checkbox" 
                                checked={allSelected} 
                                onChange={() => handleSelectAllInventory(filteredInventory)}
                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                            />
                        </th>
                        <th className="px-6 py-3 cursor-pointer hover:text-indigo-600" onClick={() => requestInventorySort('category')}>Cat <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                        <th className="px-6 py-3 cursor-pointer hover:text-indigo-600" onClick={() => requestInventorySort('brand')}>Merk & Model <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                        <th className="px-6 py-3 cursor-pointer hover:text-indigo-600" onClick={() => requestInventorySort('price')}>Prijs <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                        <th className="px-6 py-3 cursor-pointer hover:text-indigo-600" onClick={() => requestInventorySort('stock')}>Voorraad <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                        <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {filteredInventory.map((item) => (
                          <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                              <td className="px-4 py-4">
                                <input 
                                    type="checkbox" 
                                    checked={selectedInventoryIds.has(item.id)} 
                                    onChange={() => toggleInventorySelection(item.id)}
                                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                                />
                              </td>
                              <td className="px-6 py-4"><span className={`px-2 py-1 rounded-md text-xs font-bold ${getCategoryColor(item.category)}`}>{item.category}</span></td>
                              <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">{item.brand} {item.model}</td>
                              <td className="px-6 py-4 font-bold text-slate-800 dark:text-slate-200">€ {formatPrice(item.price)}</td>
                              <td className="px-6 py-4">{item.stock}</td>
                              <td className="px-6 py-4 text-right"><button onClick={() => openItemModal(item)} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800"><Pencil className="w-4 h-4" /></button></td>
                          </tr>
                      ))}
                  </tbody>
              </table>
          </div>
      </div>
  );
}